import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import { DatesService } from '@common/services';

import { PopupRangeStore, PopupCalendarConfig } from '../../utils';
import { VisualDay } from '../../models';
import { PopupRangeService } from '../../utils/popup-range.service';

@Component({
    selector: 'aa-popup-calendar',
    templateUrl: './popup-calendar.component.html',
    styleUrls: [
        '../shared.scss',
        './popup-calendar.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupCalendarComponent implements OnInit, OnDestroy
{
    @Input() isSinceCalendar: boolean;
    @Output() fromCalendarDateChanged: EventEmitter<DateTime> = new EventEmitter<DateTime>();
    public rows: number[] = Array(6).fill(0).map((_, i) => i);
    public cols: number[] = Array(7).fill(0).map((_, i) => i);
    public visualDays: VisualDay[][] = [];
    public cfg: PopupCalendarConfig = new PopupCalendarConfig();
    public legendDate: { date: string };
    private destroyator = new Subject();

    constructor (
        private popupRangeService: PopupRangeService,
        private datesService: DatesService
    )
    {
    }

    ngOnInit ()
    {
        this.popupRangeService.rangeConfig
            .pipe(
                tap((prs: PopupRangeStore) =>
                {
                    this.buildLegendTitle(this.isSinceCalendar ? prs.currentSince : prs.currentUntil);
                    this.buildCalendar(prs);
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnDestroy ()
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    onDayClick (visualDay: VisualDay)
    {
        this.buildLegendTitle(visualDay.date);
        this.popupRangeService.onDayClick(this.isSinceCalendar, visualDay.date);
    }

    public buildCalendar (prs: PopupRangeStore)
    {
        const firstDay = (this.isSinceCalendar ? prs.firstDaySinceCalendar : prs.firstDayUntilCalendar).startOf('day');
        let currentDay: DateTime = firstDay.startOf('week');

        for (let row = 0; row < 7; row++)
        {
            this.visualDays[row] = [];
            for (let col = 0; col < 7; col++)
            {
                this.visualDays[row][col] = new VisualDay(
                    currentDay,
                    firstDay,
                    prs.minDate,
                    prs.maxDate,
                    prs.currentSince,
                    prs.currentUntil
                );
                currentDay = currentDay.plus({ day: 1 });
            }
        }
    }

    private buildLegendTitle (dt: DateTime):void
    {
        this.legendDate = {
            date: this.datesService.DateTimeToDateHour(dt)
        };
    }
}
